import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'

/** TRANSLATION */
import { useTranslation } from 'react-i18next'

/** API */
import PositionApi from 'api/setting/personnel-managements/position.api'

/** UTILS || SLICE */
import { showModal, submitModal, unSubmitModal } from 'app/slice/modal.slice'
import { isCreate, isDelete, isUpdate, isView } from 'utils/verification.utils'

/** COMPONENT */
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import { Box, Grid, InputAdornment, Typography } from '@mui/material'
import HeaderCard from 'component/CardCustom/HeaderCard'
import { notiError, notiSuccess } from 'component/notifications/notifications'
import TableCustom from 'component/Table/TableCustom'
import TableRowCommon from 'component/Table/TableRowCommon'
import MainButton from 'new-components/buttons/main-button'
import SwitchButton from 'new-components/buttons/switch-button'
import SelectInput from 'new-components/inputs/select-input'
import TextInput from 'new-components/inputs/text-input'
import AlertModal from 'new-components/modal/alert-modal'
import PageModal from 'new-components/modal/page-modal'
import SnackbarComponent from 'new-components/snackbar/snackbar-component'

const permissions = {
  isCreate: isCreate(),
  isDelete: isDelete(),
  isUpdate: isUpdate(),
  isView: isView()
}

const initStateErrorMessage = {
  POSITION_NAME: ``,
  IS_DUPLICATE_POSITION_NAME: ``,
  POSITION_NAME_STRING_BASE: ``,
  POSITION_NAME_STRING_EMPTY: ``,
  POSITION_NAME_MAXIMUM_LENGTH: ``,
  POSITION_NAME_ANY_REQUIRED: ``,

  POSITION_NAME_EN: ``,
  IS_DUPLICATE_POSITION_NAME_EN: ``,
  POSITION_NAME_EN_STRING_BASE: ``,
  POSITION_NAME_EN_STRING_EMPTY: ``,
  POSITION_NAME_EN_MAXIMUM_LENGTH: ``,
  POSITION_NAME_EN_ANY_REQUIRED: ``
}
export default function Position() {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [positions, setPositions] = useState([])

  const [positionId, setPositionId] = useState(Number)
  const [positionName, setPositionName] = useState('')
  const [positionNameEn, setPositionNameEn] = useState('')
  const [positionStatus, setPositionStatus] = useState('')

  const [errorMessage, setErrorMessage] = useState(initStateErrorMessage)

  /** PAGINATION  */
  const [page, setPage] = useState(1)
  const [pageLimit, setPageLimit] = useState(10)
  const [rowCount, setRowCount] = useState(0)
  const [sortBy, setSortBy] = useState('createAt')
  const [sortType, setSortType] = useState('ASC')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('ALL')
  const [disabledSubmit, setDisabledSubmit] = useState(false)
  const [isOpenAlertModal, setIsOpenAlertModal] = useState(false)
  const [statusAlertModal, setStatusAlertModal] = useState('')
  const [id, setId] = useState<number>(0)
  const [name, setName] = useState('')
  const [nameEn, setNameEn] = useState('')
  const [isOpenSnack, setIsOpenSnack] = useState(false)
  const [typeSnack, setTypeSnack] = useState<any>("success")
  const [labelSnack, setLabelSnack] = useState('')
  const [isOpenPageModal, setIsOpenPageModal] = useState(false)

  const loadData = async () => {
    try {
      const resp = await PositionApi.findAll({
        page: page,
        pageLimit: pageLimit,
        search: search,
        sortBy: sortBy,
        sortType: sortType,
        status: status === 'ALL' ? '' : status
      })
      if (resp.status === 200) {
        setRowCount(resp.headers['x-total'])
        setPositions(resp.data)
      } else {
        setPositions([])
      }
    } catch (error) {
      notiError('ERROR')
    }
  }

  useEffect(() => {
    loadData()
  }, [page, pageLimit, sortBy, sortType])

  const submit = () => {
    if (!positionName) return setErrorMessage({ ...errorMessage, ...{ POSITION_NAME: t(`POSITION.MESSAGE.POSITION_NAME`) } })

    dispatch(submitModal())
    setDisabledSubmit(true)
    PositionApi.create(positionName, positionNameEn)
      .then((res: any) => {
        if (res.status !== undefined && res.status === 201) {
          resetForm()
          loadData()
          setIsOpenPageModal(false)
          notiSuccess(t(`POSITION.MESSAGE.SUCCESS.CREATE`), '', null, null)
        } else {
          const err = res.response.data
          setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`POSITION.MESSAGE.${err.message}`) } })
        }
      })
      .catch((e) => {
        const err = e.response.data
        setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`POSITION.MESSAGE.${err.message}`) } })
      }).finally(() => setDisabledSubmit(false))
  }

  const submitEdit = () => {
    if (!positionName) return setErrorMessage({ ...errorMessage, ...{ POSITION_NAME: t(`POSITION.MESSAGE.POSITION_NAME`) } })

    dispatch(submitModal())
    setDisabledSubmit(true)
    PositionApi.update(positionId, {
      positionName,
      positionNameEn,
      status: positionStatus
    })
      .then((res: any) => {
        if (res.status !== undefined && res.status === 200) {
          resetForm()
          loadData()
          setIsOpenPageModal(false)
          notiSuccess(t(`POSITION.MESSAGE.SUCCESS.UPDATE`), '', null, null)
        } else {
          const err = res.response.data
          setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`POSITION.MESSAGE.${err.message}`) } })
        }
      })
      .catch((e) => {
        const err = e.response.data
        setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`POSITION.MESSAGE.${err.message}`) } })
        dispatch(unSubmitModal())
      }).finally(() => setDisabledSubmit(false))
  }

  const clearErrorMessage = () => {
    setErrorMessage(initStateErrorMessage)
  }

  const resetForm = () => {
    setPositionId(0)
    setPositionName('')
    setPositionNameEn('')
    setPositionStatus('')
    setIsOpenPageModal(false)
    clearErrorMessage()
  }
  const onActive = () => {
    if (id) {
      PositionApi.updateStatus(id, statusAlertModal).then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("success")
          setLabelSnack(t('POSITION.MESSAGE.SUCCESS.UPDATE_STATUS'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("error")
          setLabelSnack(t('POSITION.MESSAGE.ERROR'))
        }
      })
    }
  }
  const onDelete = () => {
    if (id) {
      PositionApi.remove(id).then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("success")
          setLabelSnack(t('POSITION.MESSAGE.SUCCESS.DELETE'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("error")
          setLabelSnack(t('POSITION.MESSAGE.ERROR'))
        }
      })
    }
  }

  const onEdit = (data: any) => {
    const { positionId: personalPositionId, positionName: personalPositionName, positionNameEn: personalPositionNameEn, status: personalPositionStatus } = data
    setIsOpenPageModal(true)
    setPositionId(personalPositionId)
    setPositionName(personalPositionName)
    setPositionNameEn(personalPositionNameEn)
    setPositionStatus(personalPositionStatus)
    dispatch(showModal())
  }

  const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
    setSortType(sortTypeVal)
    setSortBy(sortByVal)
  }

  const handleChangePage = (val: number) => {
    setPage(val)
  }

  const handleChangeRowsPerPage = (limit: number) => {
    setPageLimit(limit)
  }

  const handleChangeStatus = (statusVal: string) => {
    setStatus(statusVal)
  }

  const headCells = [
    { id: 'no', disablePadding: false, label: '#' },
    { id: 'positionName', disablePadding: false, label: t('POSITION.TABLE.CELL.POSITION_NAME') },
    { id: 'positionNameEn', disablePadding: false, label: t('POSITION.TABLE.CELL.POSITION_NAME_EN') },
    { id: 'status', disablePadding: false, label: t('POSITION.TABLE.CELL.STATUS') },
    { id: 'updatedBy', disablePadding: false, label: t('POSITION.TABLE.CELL.UPDATED_BY') },
    { id: 'action', disablePadding: false, label: 'Action' }
  ]

  const renderData = (objData: any, no: number) => {
    no = page * pageLimit - pageLimit + no + 1
    const { positionId: personalPositionId, positionName: personalPositionName, positionNameEn: personalPositionNameEn, isDefault, updatedBy, updatedAt, status: personalPositionStatus } = objData

    const statusBtnActive = personalPositionStatus === 'ACTIVE' ? true : permissions.isUpdate.disabled
    const statusBtnInActive = personalPositionStatus === 'INACTIVE' ? true : permissions.isUpdate.disabled

    const objRenderData = {
      key: personalPositionId,
      id: personalPositionId,
      obj: objData,
      columns: [
        { option: 'TEXT', align: 'center', label: no },
        { option: 'TEXT', align: 'left', label: personalPositionName },
        { option: 'TEXT', align: 'left', label: personalPositionNameEn },
        { option: 'STATUS', align: 'center', label: personalPositionStatus },
        { option: 'UPDATE_BY', align: 'center', label: { updatedBy: updatedBy, updatedAt: updatedAt } },
        {
          option: 'ACTION',
          align: 'center',
          label: 'action',
          disabled: isDefault === `1`,
          values: [
            { option: 'STATUS_ACTIVE', label: t(`STATUS.ACTIVE`), disabled: statusBtnActive },
            { option: 'STATUS_INACTIVE', label: t(`STATUS.INACTIVE`), disabled: statusBtnInActive },
            { option: 'DIVIDER', label: '', disabled: false },
            { option: 'EDIT', label: t(`BUTTON.EDIT`), disabled: permissions.isUpdate.disabled },
            { option: 'DELETE', label: t(`BUTTON.DELETE`), disabled: permissions.isDelete.disabled }
          ]
        },
      ]
    }
    return (
      <TableRowCommon
        {...objRenderData}
        onactive={() => handleSubmitModal(Number(objRenderData.id), 'ACTIVE', objRenderData.obj)}
        oninactive={() => handleSubmitModal(Number(objRenderData.id), 'INACTIVE', objRenderData.obj)}
        onedit={() => onEdit(objRenderData.obj)}
        ondelete={() => handleSubmitModal(Number(objRenderData.id), 'DELETE', objRenderData.obj)}
      />
    )
  }

  const handleSubmitModal = (id: number, status: string, obj?: any) => {
    setStatusAlertModal(status)
    setId(obj.positionId)
    setIsOpenAlertModal(true)
    setName(obj.positionName)
    setNameEn(obj.positionNameEn)
  }

  return (
    <Box>
      <AlertModal
        isOpen={isOpenAlertModal}
        title={t(`POSITION.ALERT.${statusAlertModal === 'DELETE' ? 'TITLE_DELETE' : 'TITLE_ACTIVE'}`)}
        message={t(`POSITION.ALERT.${statusAlertModal === 'DELETE' ? 'CONFIRM_DELETE_' : 'CONFIRM_STATUS'}`, { positionName: name, positionNameEn: nameEn && `(${nameEn})`, status: t(`POSITION.ALERT.${statusAlertModal}`) })}
        type={statusAlertModal === 'DELETE' ? 'error' : 'warning'}
        handleClose={() => setIsOpenAlertModal(false)}
        onCancel={{ onClick: () => setIsOpenAlertModal(false), title: t('BUTTON.CANCEL') }}
        onSubmit={{ onClick: statusAlertModal === 'DELETE' ? onDelete : onActive, title: t('BUTTON.SUBMIT') }}
      />
      <SnackbarComponent
        onOpen={isOpenSnack}
        onClose={() => setIsOpenSnack(false)}
        label={labelSnack}
        type={typeSnack}
        transform={`translateX(calc(100vw - 100% - 32px)) translateY(calc(-100vh + 100% + ${window.innerHeight - 50}px))`}
        autoHideTime={3000}
      />
      <HeaderCard text={t('POSITION.TITLE')} />
      <Box paddingX="24px" paddingTop="16px">
        <Grid container spacing="16px">
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <TextInput
              label={t('POSITION.SEARCH')}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value)
              }}
              fullWidth
              inputProps={{
                endAdornment: (
                  <InputAdornment position="end" >
                    {!search ? <FontAwesomeIcon icon={faSearch} style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 15 }} /> :
                      <FontAwesomeIcon
                        icon={faTimes}
                        onClick={() => {
                          setSearch('');
                        }}
                        style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 17, cursor: 'pointer' }} />}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <SelectInput
              onChange={(event) => {
                const value = event.target.value
                if (value) {
                  handleChangeStatus(value)
                }
              }}
              options={[
                { value: 'ALL', name: t('STATUS.ALL') },
                { value: 'ACTIVE', name: t('STATUS.ACTIVE') },
                { value: 'INACTIVE', name: t('STATUS.INACTIVE') }
              ]}
              label={t('STATUS.LABEL')}
              value={status}
              width="100%"
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} xl={8}>
            <Box display={'flex'} justifyContent={'space-between'}>
              <MainButton
                title={t('INPUT.SEARCH')}
                type='primary'
                variant='contained'
                style={{ width: '120px' }}
                onClick={() => {
                  setPage(1)
                  loadData()
                }} />

              <MainButton
                title={t('POSITION.BUTTON.ADD')}
                disabled={permissions.isCreate.disabled}
                type='primary'
                variant='contained'
                onClick={() => setIsOpenPageModal(true)}
                startIcon={<AddCircleIcon />}
              />
            </Box>
          </Grid>
        </Grid>
        <Box paddingTop="24px">
          <TableCustom
            page={page}
            pageLimit={pageLimit}
            sortType={sortType}
            sortBy={sortBy}
            onSort={onRequestSort}
            setPageLimit={handleChangeRowsPerPage}
            setPage={handleChangePage}
            rowCount={rowCount}
            headCells={headCells}
            rowsData={positions.map((val, i) => {
              return renderData(val, i)
            })}
          />
        </Box>
        <PageModal
          isOpen={isOpenPageModal}
          title={positionId ? t('POSITION.TITLE_UPDATE') : t('POSITION.TITLE_CREATE')}
          size='XS'
          handleClose={resetForm}
          onCancel={{ onClick: resetForm, title: t('MATERIAL_INSTRUCTION.BUTTON.CANCEL') }}
          onSubmit={{ onClick: positionId ? submitEdit : submit, title: t('MATERIAL_INSTRUCTION.BUTTON.SAVE'), disabled: disabledSubmit }}
        >
          <Box display="flex" flexDirection="column" gap={'16px'}>
            {positionId ? <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography>{t('INPUT.LABEL_STATUS')}</Typography>
              <SwitchButton onChange={() => setPositionStatus(positionStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE')} checked={positionStatus === 'ACTIVE' ? true : false} />
            </Box> : ''}
            <TextInput
              onChange={(event) => {
                setPositionName(event.target.value)
                clearErrorMessage()
              }}
              value={positionName}
              label={t('POSITION.INPUT.POSITION_NAME')}
              helperText={
                errorMessage.POSITION_NAME ||
                errorMessage.IS_DUPLICATE_POSITION_NAME ||
                errorMessage.POSITION_NAME_STRING_BASE ||
                errorMessage.POSITION_NAME_STRING_EMPTY ||
                errorMessage.POSITION_NAME_MAXIMUM_LENGTH ||
                errorMessage.POSITION_NAME_ANY_REQUIRED
              }
              required
            />
            <TextInput
              key="positionNameEn"
              onChange={(event) => {
                setPositionNameEn(event.target.value)
                clearErrorMessage()
              }}
              value={positionNameEn}
              helperText={
                errorMessage.POSITION_NAME_EN ||
                errorMessage.IS_DUPLICATE_POSITION_NAME_EN ||
                errorMessage.POSITION_NAME_EN_STRING_BASE ||
                errorMessage.POSITION_NAME_EN_STRING_EMPTY ||
                errorMessage.POSITION_NAME_EN_MAXIMUM_LENGTH ||
                errorMessage.POSITION_NAME_EN_ANY_REQUIRED
              }
              label={t('POSITION.INPUT.POSITION_NAME_EN')}
            />
          </Box>
        </PageModal>
      </Box>
    </Box>
  )
}
